import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import { required } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import router from "@/router";
import axios from "@axios";
import { reactive, toRefs, ref } from "@vue/composition-api";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { v4 as uuidv4 } from "uuid";
import { fieldStructure } from "@/components/securitySelfAuditTemplateBuilder/config";

export default function useSimulacrumTemplateBuilderConfig() {
  const state = reactive({
    items: [
      {
        name: "",
        type: "facility",
        logic: [],
        errors: {},
        item_id: "facility",
        options: { required: false, fieldTypeCfg: { data: [] } },
        hasErrors: false,
        isDefault: true,
      },
    ],
    isValid: false,
    templateId: null,
    isUpdating: false,
  });

  const templateName = ref("");
  const idTenant = ref(null);

  const toast = useToast();
  const { refFormObserver, getValidationState } = formValidation();
  const { t } = useI18nUtils();
  const trans = {
    "messages.edit_save.successfully_created": t("messages.edit_save.successfully_created"),
    "messages.edit_save.updated_successfully": t("messages.edit_save.updated_successfully"),
  };

  const addItem = () => {
    const item = JSON.parse(JSON.stringify(fieldStructure));
    item.item_id = uuidv4();
    state.items.push(item);
  };

  const setItems = (val) => {
    state.items = Array.isArray(val) ? val : state.items;
  };

  const setIsValid = (val) => {
    state.isValid = Boolean(val);
  };

  const pickId = (resp, fullResponse) => {
    const tryIds = [
      resp?.id,
      resp?.data?.id,
      resp?.id_template,
      resp?.data?.id_template,
      resp?.template?.id,
    ];
    const found = tryIds.find((v) => v != null);
    if (!found && fullResponse?.headers?.location) {
      const m = String(fullResponse.headers.location).match(/\/builder\/(\d+)(?:$|\/|\?)/);
      if (m && m[1]) return String(m[1]);
    }
    return found ? String(found) : null;
  };

  const storeTemplate = async (name, data, extra = {}) => {
    const payload = { name, data, ...extra };

    if (!state.isUpdating) {
      const response = await axios.post("v1/simulacrum_templates", payload);
      const resp = response.data;

      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.edit_save.successfully_created"],
          icon: "CheckIcon",
          variant: "success",
        },
      });

      const newId = pickId(resp, response);
      if (newId) {
        state.isUpdating = true;
        state.templateId = newId;
        router.replace({ name: "configSimulacrumTemplateEdit", params: { id_template: newId } });
      } else {
        toast({
          component: ToastificationContent,
          props: { title: "Guardado, pero no llegó el ID.", icon: "AlertTriangleIcon", variant: "warning" },
        });
      }
    } else {
      const response = await axios.put(`v1/simulacrum_templates/${state.templateId}`, payload);
      const resp = response.data;

      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.edit_save.updated_successfully"],
          icon: "CheckIcon",
          variant: "success",
        },
      });

      const ensuredId = pickId(resp, response) || state.templateId;
      state.templateId = ensuredId;
      const cur = router.currentRoute;
      if (cur.name !== "configSimulacrumTemplateEdit" || cur.params.id_template !== String(ensuredId)) {
        router.replace({ name: "configSimulacrumTemplateEdit", params: { id_template: String(ensuredId) } });
      }
    }
  };

  const save = async (extra = {}, itemsOverride = null) => {
    const validation = await refFormObserver.value.validate();
    if (!validation) return;
    const dataToStore = Array.isArray(itemsOverride) ? itemsOverride : state.items;
    await storeTemplate(templateName.value, dataToStore, extra);
  };

  const initFromRoute = async () => {
    const id_template =
      router.currentRoute.params.id_template ||
      router.currentRoute.params.id ||
      null;

    if (!id_template) return;

    state.isUpdating = true;
    state.templateId = String(id_template);

    const { data } = await axios.get(`v1/simulacrum_templates/${id_template}`);

    templateName.value = data.name || "";
    if (Array.isArray(data.data)) state.items = data.data;

    if (data.id_tenant) idTenant.value = data.id_tenant;
    else if (data.tenant && data.tenant.id) idTenant.value = data.tenant.id;
  };

  return {
    addItem,
    save,
    setItems,
    setIsValid,
    ...toRefs(state),
    templateName,
    idTenant,
    refFormObserver,
    getValidationState,
    required,
    initFromRoute,
  };
}
